import { useCallback, useEffect, useState } from 'react';
import {
  AccessibilityInfo,
  Keyboard,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
} from 'react-native';

import { useLoggerContext } from './loggerContext';
import { WorkoutMetaFooter } from './WorkoutMetaFooter';
import { WorkoutNameEditor } from './WorkoutNameEditor';
import { useLoggerGymChip } from './LoggerGymChip';
import { useInWorkoutTargets } from './useInWorkoutTargets';
import { usePlanCues } from './usePlanCues';
import { TimelineFocusSection } from './TimelineFocusSection';
import { TimelineHistorySheet } from './TimelineHistorySheet';
import { LoggerCommitPrompts } from './LoggerCommitPrompts';
import { TimelineFeedSection } from './TimelineFeedSection';
import { getBestE1rmForSlug, useEntryHistory } from './useEntryHistory';
import { useEffortCommit } from './useEffortCommit';
import { useTimelineLogger } from './useTimelineLogger';
import { useTimelineNotes } from './useTimelineNotes';
import { useWorkBoutAutoComplete } from './useWorkBoutAutoComplete';

// Timeline-logger screen (Phase 2). The alternative logger body, rendered in
// place of the card list when Settings → Preferences → Logger is set to
// "Timeline". Reads the SAME logger context bag the card list reads, so it
// drives the SAME flat draft store — switching modes never forks the data
// (next/timeline-logger.md §5; the old card logger stays the live fallback).
//
// Three regions: a roster strip (switch the active exercise), the FocusPanel
// (the single data-entry point), and the reverse-chronological TimelineList
// feed. The focus state machine + the set/exercise handlers live in
// useTimelineLogger (Track R extraction); this file is the render shell only.
export function TimelineLoggerScreen() {
  const ctx = useLoggerContext();
  const {
    draft,
    energyReadout,
    dispatch,
    styles,
    theme,
    t,
    i18n,
    getExerciseNameBySlug,
    submitting,
    totals,
    catalog,
    error,
    serverError,
    defaultUnit,
    setPickerOpen,
    setEffortScaleOpen,
    onCopyHistory,
    nameSuggestion,
    postSaveSummary,
    summarySessionId,
    sessionResult,
    summaryCardRef,
    headerSharing,
    triggerHeaderShare,
    hold,
  } = ctx;

  // Kept as one object as well as destructured: TimelineFocusSection takes the
  // whole bag, so the ~20 values its JSX reads cross the new file boundary as a
  // single typed prop instead of twenty hand-forwarded ones (CN-27).
  const tl = useTimelineLogger(ctx);
  const {
    activeEntry,
    activeEntryIdx,
    boundSet,
    boundSetIdx,
    activeExercise,
    effortScale,
    timelineRows,
    focusRow,
    onLogSet,
    onLogFail,
    onToggleFail,
    failPrompt,
    onConfirmFail,
    onCancelFail,
    amrapConfirm,
    closeHistory,
    rest,
    restFreezesNavigation,
    failFlash,
  } = tl;

  // HI-1 slice 2: a prescribed countdown bout ends itself at zero, through the
  // same onLogSet the athlete's tap uses. Mounted here because this is where
  // the timer and the completion leg already meet (see captureTimer below) —
  // the hook itself writes nothing.
  useWorkBoutAutoComplete({
    hold,
    entries: draft.entries,
    activeEntryIdx,
    boundSetIdx,
    onLogSet,
  });

  // AP-3b — the in-workout contextual meter's rows for the active exercise,
  // with this live session's sets already folded into the weekly aggregate.
  const targetRows = useInWorkoutTargets(
    draft,
    activeExercise ?? null,
    catalog,
    effortScale,
    ctx.deps,
  );

  // Effort-meter commit bag (AP-0b/0c): routes a meter tap onto the existing
  // commit legs (onLogSet with the tapped rir / onLogFail / onToggleFail) and
  // derives the meter's value/failed from the bound set. ALL set kinds — a
  // run or a plank carries effort like a squat («quando corro non mi sento
  // mai uguale»); schema and payload already accept rir on every kind.
  const effort = useEffortCommit({
    boundSet,
    activeEntryIdx,
    boundSetIdx,
    dispatch,
    onLogSet,
    onLogFail,
    onToggleFail,
    onOpenEffortScale: () => setEffortScaleOpen(true),
    willPromptAmrap: amrapConfirm.shouldPrompt,
    // A band tap on a set whose timer is running also STOPS it and hands over
    // the seconds (MX-5 slice 3). Bound to the set being logged: capture()
    // returns null — and leaves the timer alone — when nothing is running or
    // when what's running belongs to another set, so the ordinary reps tap goes
    // through untouched and a plank running elsewhere is never harvested here.
    captureTimer: () => hold.capture(activeEntryIdx, boundSetIdx),
  });

  // Per-set / per-exercise coach cues re-resolved from the source program so
  // the 🎯 + 💬 reappear on EDIT / REPEAT (they're display-only, never saved —
  // Antenore 2026-06-19 regression). Same reference + offline cache as the
  // header "?" (useCoachNotes). The active entry's cues feed the FocusPanel as
  // a fallback; a live plan-start already carries them on the draft.
  const planCues = usePlanCues(draft.fromPlanId ?? draft.coachNotesRef, ctx.deps.apiFetch);
  const activeCues = activeEntry
    ? planCues.get(activeEntry.exerciseId)
    : undefined;

  // Every exercise fully logged? Drives the FocusPanel-slot placeholder copy:
  // "all logged — add another or save" vs the start-of-workout "pick above".
  const allLogged =
    draft.entries.length > 0 &&
    draft.entries.every(
      (e) => e.sets.length > 0 && e.sets.every((s) => s.completedAt !== null),
    );

  // Timeline notes (Solution C): merge set rows + note events into one
  // chronological feed + own the composer handlers. The composer is the single
  // note-authoring surface (the per-entry 📝 menu is retired).
  // ⚠️ The note's context is WHICH EXERCISE, so it takes the catalog slug —
  // NOT the logger cursor, which since LG-39 carries the row's entryId. The
  // two used to be the same string and this argument silently meant both.
  const { feed, onSendNote, onDeleteNote } = useTimelineNotes(
    ctx,
    timelineRows,
    activeEntry?.exerciseId ?? null,
  );

  // Workout title: shown as compact text (the user's name, or the live
  // auto-suggestion when blank) with a ✎ to switch to an inline input.
  const [editingName, setEditingName] = useState(false);
  // EQ-1 slice D — the place rides that same row; the hook hands back the chip
  // and the tray separately so each can be placed where it belongs.
  const gymChip = useLoggerGymChip({
    value: draft.location ?? null,
    dispatch,
    // EQ-1-GYM-DEFAULT — the ★ that elects a default place, through the existing
    // profile door the rest-default picker already uses (hostDeps.patchProfile).
    defaultLocation: ctx.deps.profile.defaultLocation,
    patchProfile: ctx.deps.patchProfile,
    notify: ctx.deps.notify,
  });
  // Leave edit mode the moment a save starts so the title never gets stuck
  // showing a disabled input during the submit window.
  useEffect(() => {
    if (submitting) setEditingName(false);
  }, [submitting]);

  // Live "% of best" (LG-20): getBestE1rmForSlug reads the module-scope
  // recent-history cache, which warms asynchronously on Logger open. This
  // screen must SUBSCRIBE to that cache's readiness — otherwise it never
  // re-renders when the cache lands, so the per-set e1RM % only appeared
  // after save (the full re-render). Consuming `ready` wakes the timeline the
  // moment history is available; keying the callback on it also refreshes the
  // value should TimelineList ever become memoised. (The e1RM VALUE itself is
  // already computed client-side in buildTimelineRows, so it shows live even
  // when the cache is cold; only the comparison waits on history.)
  const { ready: historyReady } = useEntryHistory('');
  const bestE1rmForSlug = useCallback(
    (slug: string) => getBestE1rmForSlug(slug),
    // historyReady is deliberately "unnecessary" to eslint: getBestE1rmForSlug
    // reads a MODULE-scope cache, so the rule sees no reactive input at all.
    // ⚠️ It is NOT what fixes LG-20 today — the re-render comes from
    // useEntryHistory('') subscribing this component to `ready` (see the
    // paragraph above). The dep is the insurance the paragraph describes: it
    // re-keys the callback should TimelineList ever become memoised. No
    // consumer keys on its identity right now, so dropping it would change
    // nothing — which is exactly why it must not be dropped by accident.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [historyReady],
  );

  // LG-29 — while a rest counts, freeze the logger's editing surfaces so a stray
  // tap can't rewrite the set just logged (the LG-26 loss). `rest` is non-null
  // for the WHOLE countdown: the set just logged for the first ~2.5s, then the
  // next set/exercise the transition hands over to — both frozen, the timer the
  // only thing live. The freeze lives in the components: FocusPanel takes
  // `restFrozen`, and the roster / add-exercise / timeline feed each sit in a
  // <TouchLock>. Because those navigation surfaces are frozen, focus can only be
  // where the transition put it — which is why the rest prop no longer needs to
  // check whose card the timer belongs to (useTimelineLogger). The ✕ ends the
  // rest to edit cold; to jump exercise you stop the timer, then restart it from
  // the header pill.
  const restFrozen = rest !== null;
  // pointerEvents="none" blocks NEW touches but does NOT blur a TextInput that
  // was focused when the rest began (the athlete had just typed reps) — the soft
  // keyboard would keep writing into the now-untouchable field. Dismiss it the
  // instant the freeze engages, or the fix has a hole shaped like the bug.
  //
  // A sighted athlete SEES the freeze: the effort meter becomes the rest
  // countdown, a loud state change. A screen-reader user gets nothing — the
  // fields they were touching just leave the a11y tree (accessibilityElements
  // Hidden) with no word why. So announce it, the same way a fail does
  // (failBadgeA11y). No-op when no screen reader is active, so sighted users
  // never hear it and the visible rest row remains their only signal.
  useEffect(() => {
    if (!restFrozen) return;
    Keyboard.dismiss();
    AccessibilityInfo.announceForAccessibility(
      t('mobile.logger.restLockedA11y', {
        defaultValue: 'Editing locked while resting. Tap skip to edit.',
      }),
    );
  }, [restFrozen, t]);

  return (
    <KeyboardAvoidingView
      style={styles.flex}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
    >
      <ScrollView
        contentContainerStyle={styles.body}
        keyboardShouldPersistTaps="handled"
      >
        {/* EQ-1 slice D — WHERE this session is happening, on the session's
            own header row beside its name (Antenore, 2026-08-13). It sat in
            the roster header first and read as "completamente sconnesso":
            wedged between the EXERCISES label and the list's own chevron it
            looked like a control of the exercise list, which it is not. The
            place is a fact of the SESSION, so it belongs where the session
            names itself. The tray opens full-width underneath. */}
        <WorkoutNameEditor
          name={draft.name}
          nameSuggestion={nameSuggestion}
          editing={editingName}
          onStartEdit={() => setEditingName(true)}
          onStopEdit={() => setEditingName(false)}
          onChangeName={(text) => dispatch({ type: 'set_name', name: text })}
          submitting={submitting}
          trailing={gymChip.trigger}
          styles={styles}
          theme={theme}
          t={t}
        />
        {gymChip.tray}

        {/* Roster strip + FocusPanel — split to TimelineFocusSection when the
            shell sat at exactly 500 lines with no margin (CN-27), the same
            mould TimelineFeedSection set for the bottom of the screen. */}
        <TimelineFocusSection
          ctx={ctx}
          tl={tl}
          targetRows={targetRows}
          effort={effort}
          activeCues={activeCues}
          allLogged={allLogged}
          restFrozen={restFrozen}
        />

        {/* Note composer + add-exercise + reverse-chronological feed — split
            to TimelineFeedSection when LG-29's freeze wraps pushed this shell
            over the 500-line cap (CN-27). The composer stays live during a
            rest; the way out is cold, except on a last-set rest (TM-12). */}
        <TimelineFeedSection
          ctx={ctx}
          feed={feed}
          restFrozen={restFreezesNavigation}
          onSendNote={onSendNote}
          onDeleteNote={onDeleteNote}
          onFocusRow={focusRow}
          bestE1rmForSlug={bestE1rmForSlug}
          failFlash={failFlash}
        />

        <WorkoutMetaFooter
          postSaveSummary={postSaveSummary}
          summarySessionId={summarySessionId}
          sessionResultValue={sessionResult.result}
          energyReadout={energyReadout}
          totals={totals}
          draft={draft}
          error={error}
          serverError={serverError}
          headerSharing={headerSharing}
          triggerHeaderShare={triggerHeaderShare}
          dispatch={dispatch}
          defaultUnit={defaultUnit}
          locale={i18n.language}
          setPickerOpen={setPickerOpen}
          summaryCardRef={summaryCardRef}
          hideAddExercise
          styles={styles}
          theme={theme}
          t={t}
          PostSessionSummaryCard={ctx.deps.PostSessionSummaryCard}
          profile={ctx.deps.profile}
        />
      </ScrollView>

      {/* 🕐 history bottom sheet (extracted to TimelineHistorySheet, slice C). */}
      <TimelineHistorySheet
        entry={activeEntry}
        onClose={closeHistory}
        onCopy={(sourceEntry) => {
          onCopyHistory(activeEntryIdx, sourceEntry);
          closeHistory();
        }}
        locale={i18n.language}
        unit={defaultUnit}
        getExerciseNameBySlug={getExerciseNameBySlug}
        t={t}
        theme={theme}
      />

      {/* The prompts raised BY a completion — fail rep-count (AP-0p) and the
          AMRAP question (LG-26). One host so they cannot stack. */}
      <LoggerCommitPrompts
        failPrompt={failPrompt}
        onConfirmFail={onConfirmFail}
        onCancelFail={onCancelFail}
        amrapPrompt={amrapConfirm.prompt}
        onConfirmAmrap={amrapConfirm.confirm}
        onDismissAmrap={amrapConfirm.dismiss}
      />
    </KeyboardAvoidingView>
  );
}
